import {
  FETCHING_CLUSTERS,
  FETCHING_CLUSTERS_SUCCEEDED,
  FETCHING_CLUSTERS_FAILED,
  FETCHING_CLUSTER,
  FETCHING_CLUSTER_SUCCEEDED,
  FETCHING_CLUSTER_FAILED,
  CREATING_CLUSTER,
  CREATING_CLUSTER_SUCCEEDED,
  CREATING_CLUSTER_FAILED,
  DELETING_CLUSTER,
  DELETING_CLUSTER_SUCCEEDED,
  DELETING_CLUSTER_FAILED,
  UPDATING_CLUSTER,
  UPDATING_CLUSTER_SUCCEEDED,
  UPDATING_CLUSTER_FAILED,
  FETCHING_ASSETS_CONTENT_MAP,
  FETCHING_ASSETS_CONTENT_MAP_SUCCEEDED,
  FETCHING_ASSETS_CONTENT_MAP_FAILED,
  FETCHING_ASSET_ANALYTICS_SUCCEEDED,
  INVALIDATE_CONTENT_MAP_ASSETS,
  DELETE_ASSETS_CONTENT_MAP,
  DELETE_ASSETS_CONTENT_MAP_SUCCEEDED,
  DELETE_ASSETS_CONTENT_MAP_FAILED,
  UPDATE_ASSETS_CONTENT_MAP,
  UPDATE_ASSETS_CONTENT_MAP_FAILED,
  UPDATE_ASSETS_CONTENT_MAP_SUCCEEDED,
  FETCH_ASSETS_FOR_DOWNLOAD,
  FETCHING_ASSETS_FOR_DOWNLOAD_FAILED,
  FETCHING_ASSETS_FOR_DOWNLOAD_SUCCEEDED,
  INVALIDATE_ASSETS_FOR_DOWNLOAD,
  UPDATING_ASSET_SUCCEEDED,
  INVALIDATE_CONTENT_DATA,
  FETCHING_ASSETS_FOR_CONTENT_SCORE_CARD,
  FETCHING_ASSETS_FOR_CONTENT_SCORE_CARD_SUCCEEDED,
  FETCHING_ASSETS_FOR_CONTENT_SCORE_CARD_FAILED,
  FETCHING_CONTENT_GRAPH_VALUES,
  FETCHING_CONTENT_GRAPH_VALUES_SUCCEEDED,
  FETCHING_CONTENT_GRAPH_VALUES_FAILED,
  FETCHING_CONTENT_SCORE,
  FETCHING_CONTENT_SCORE_SUCCEEDED,
  FETCHING_CONTENT_SCORE_FAILED,
  UPDATING_ASSET_REQUEST
} from 'src/redux/actions/actionTypes';
import { type UnknownAction } from '@reduxjs/toolkit'

import _ from 'lodash';
import { Cluster, FetchAssetsForContentScoreErrorResponse, FetchAssetsForContentScoreRequest, FetchAssetsForContentScoreResponse, FetchAssetsRequest, FetchAssetsResponse, FetchClusterErrorResponse, FetchClusterRequest, FetchContentGraphValuesErrorResponse, FetchContentGraphValuesResponse, FetchingContentScoreErrorResponse, FetchingContentScoreResponse } from 'src/types/contentMap';
import { Asset } from 'src/types/assets';

interface ContentMapState {
  clusters: SavedClustersState,
  cluster: ClusterState,
  creatingCluster: CreatingClusterState,
  deletingCluster: DeletingClusterState,
  updatingCluster: UpdatingClusterState,
  assets: AssetsState
  contentScore: ContentScoreState
}

interface AssetsState {
  assetsList: AssetsListState,
  download: AssetsListState,
  delete: {
    loading: boolean,
    error: any,
    succeeded: boolean
  },
  update: {
    loading: boolean,
    error: any,
    succeeded: boolean
  }
}

interface AssetsListState {
  loading: boolean,
  error: any,
  items: Asset[] | null,
  succeeded: boolean,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
}
interface CreatingClusterState {
  loading: boolean,
  error: any,
  succeeded: boolean,
  item?: Cluster | null
}

interface UpdatingClusterState {
  loading: boolean,
  error: any,
  succeeded: boolean,
  item: Cluster | null
}

interface DeletingClusterState {
  loading: boolean,
  error: any,
  succeeded: boolean,
}

interface SavedClustersState {
  loading: boolean,
  error: any,
  items: Cluster[] | null,
  succeeded: boolean,
}

interface ClusterState {
  [key: string]: {
    loading: boolean,
    error: any,
    item: Cluster,
    succeeded?: boolean
  }
}
interface ContentScoreState {
  graphData: any,
  scoreData: any
}

const assetsInitialData = {
  loading: false,
  error: null,
  items: [],
  succeeded: false,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false
  },

}

const assetsInitialState: AssetsState = {
  assetsList: assetsInitialData,
  download: assetsInitialData,
  delete: {
    loading: false,
    error: null,
    succeeded: false
  },
  update: {
    loading: false,
    error: null,
    succeeded: false
  }
}

const initialState: ContentMapState = {
  clusters: {
    loading: false,
    error: null,
    items: [],
    succeeded: false
  },
  cluster: {},
  creatingCluster: {
    loading: false,
    error: null,
    succeeded: false,
    item: null
  },
  deletingCluster: {
    loading: false,
    error: null,
    succeeded: false
  },
  updatingCluster: {
    loading: false,
    error: null,
    succeeded: false,
    item: null
  },
  assets: assetsInitialState,
  contentScore: {
    graphData: {},
    scoreData: {}
  }
}

function contentMapReducer(state: ContentMapState = initialState, action: UnknownAction): ContentMapState {
  switch (action.type) {

    case FETCHING_CLUSTERS: {
      return {
        ...state,
        clusters: {
          ...state.clusters,
          loading: true,
          error: null,
          succeeded: false
        }
      }
    }

    case FETCHING_CLUSTERS_SUCCEEDED: {
      return {
        ...state,
        clusters: {
          loading: false,
          items: action.payload as Cluster[],
          error: null,
          succeeded: true
        }
      }
    }

    case FETCHING_CLUSTERS_FAILED: {
      const error = action.payload;

      return {
        ...state,
        clusters: {
          loading: false,
          error: error,
          items: [],
          succeeded: false
        }
      }
    }

    case FETCHING_CLUSTER: {
      const { id } = action.payload as FetchClusterRequest;

      const clusterData = state.cluster[id];

      if (id) {
        return {
          ...state,
          cluster: {
            ...state.cluster,
            [id]: {
              ...clusterData,
              loading: true,
              error: null,
              succeeded: false
            }
          }
        }
      }

      return state;

    }

    case FETCHING_CLUSTER_SUCCEEDED: {
      const cluster = action.payload as Cluster;
      const id = cluster.id;

      return {
        ...state,
        cluster: {
          ...state.cluster,
          [id]: {
            loading: false,
            item: cluster,
            error: null,
            succeeded: true
          }
        }
      }
    }

    case FETCHING_CLUSTER_FAILED: {
      const { id, error } = action.payload as FetchClusterErrorResponse;

      return {
        ...state,
        cluster: {
          ...state.cluster,
          [id]: {
            ...state.cluster[id],
            loading: false,
            error: error,
            succeeded: false
          }
        }
      }
    }

    case CREATING_CLUSTER: {
      return {
        ...state,
        creatingCluster: {
          loading: true,
          error: null,
          succeeded: false
        }
      }
    }

    case CREATING_CLUSTER_SUCCEEDED: {
      return {
        ...state,
        creatingCluster: {
          loading: false,
          error: null,
          succeeded: true,
          item: action.payload as Cluster
        }
      }
    }

    case CREATING_CLUSTER_FAILED: {
      const error = action.payload;

      return {
        ...state,
        creatingCluster: {
          loading: false,
          error: error,
          succeeded: false
        }
      }
    }

    case DELETING_CLUSTER: {
      return {
        ...state,
        deletingCluster: {
          loading: true,
          error: null,
          succeeded: false
        }
      }
    }

    case DELETING_CLUSTER_SUCCEEDED: {
      return {
        ...state,
        deletingCluster: {
          loading: false,
          error: null,
          succeeded: true
        }
      }
    }

    case DELETING_CLUSTER_FAILED: {
      const error = action.payload;

      return {
        ...state,
        deletingCluster: {
          loading: false,
          error: error,
          succeeded: false
        }
      }
    }

    case UPDATING_CLUSTER: {
      return {
        ...state,
        updatingCluster: {
          loading: true,
          error: null,
          succeeded: false,
          item: null
        }
      }
    }

    case UPDATING_CLUSTER_SUCCEEDED: {
      const updatedCluster = action.payload as Cluster;
      return {
        ...state,
        cluster: {
          ...state.cluster,
          [updatedCluster.id]: {
            loading: false,
            error: null,
            item: updatedCluster,
            succeeded: true
          }
        },
        updatingCluster: {
          loading: false,
          error: null,
          succeeded: true,
          item: updatedCluster
        },
        clusters:{
          ...state.clusters,
          items: _.get(state, "clusters.items", []).map((cluster) => {
            if (cluster.id === updatedCluster.id) {
              return {
                ...cluster,
                name: updatedCluster.name
              }
            }
            return cluster;
          })
        }
      }
    }

    case UPDATING_CLUSTER_FAILED: {
      const error = action.payload;

      return {
        ...state,
        updatingCluster: {
          loading: false,
          error: error,
          succeeded: false,
          item: null
        }
      }
    }

    case FETCHING_ASSETS_CONTENT_MAP: {
      const { page } = action.payload as FetchAssetsRequest;
      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...state.assets.assetsList,
            loading: true,
            error: null,
            succeeded: false,
            items: page === 0 ? [] : _.get(state, "assets.assetsList.items", []),
            pagination: {
              ...state.assets.assetsList.pagination,
              currentPage : page
            }
          }
        }
      }
    }

    case FETCHING_ASSETS_CONTENT_MAP_SUCCEEDED: {
      const { response } = action.payload as FetchAssetsResponse;

      let existingItems: Asset[] = _.get(state, "assets.assetsList.items", []);

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }
      records = _.uniqBy(records, 'id');

      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            loading: false,
            items: records,
            succeeded: true,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1
            },
            error: null
          }

        }
      }

    }

    case FETCHING_ASSETS_CONTENT_MAP_FAILED: {
      const error = action.payload;

      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...state.assets.assetsList,
            loading: false,
            error: error,
            succeeded: false
          }
        }
      }
    }

    case FETCHING_ASSET_ANALYTICS_SUCCEEDED:
      const { assetId, response } = action.payload as any;

      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...state.assets.assetsList,
            items: _.get(state, "assets.assetsList.items", []).map((asset) => {
              if (asset.id === assetId) {
                return {
                  ...asset,
                  analytics: response
                }
              }
              return asset;
            })
          }
        }
      }

    case INVALIDATE_CONTENT_MAP_ASSETS:
      return {
        ...state,
        assets: assetsInitialState
      }

    case DELETE_ASSETS_CONTENT_MAP:
      return {
        ...state,
        assets: {
          ...state.assets,
          delete: {
            loading: true,
            error: null,
            succeeded: false
          }
        }
      }

    case DELETE_ASSETS_CONTENT_MAP_SUCCEEDED:
      return {
        ...state,
        assets: {
          ...state.assets,
          delete: {
            loading: false,
            error: null,
            succeeded: true
          }
        }
      }

    case DELETE_ASSETS_CONTENT_MAP_FAILED:
      const error = action.payload;

      return {
        ...state,
        assets: {
          ...state.assets,
          delete: {
            loading: false,
            error: error,
            succeeded: false
          }
        }
      }

    case UPDATE_ASSETS_CONTENT_MAP:
      return {
        ...state,
        assets: {
          ...state.assets,
          update: {
            loading: true,
            error: null,
            succeeded: false
          }
        }
      }

    case UPDATE_ASSETS_CONTENT_MAP_SUCCEEDED:
      return {
        ...state,
        assets: {
          ...state.assets,
          update: {
            loading: false,
            error: null,
            succeeded: true
          }
        }
      }

    case UPDATE_ASSETS_CONTENT_MAP_FAILED:
      const updateError = action.payload;

      return {
        ...state,
        assets: {
          ...state.assets,
          update: {
            loading: false,
            error: updateError,
            succeeded: false
          }
        }
      }

    case FETCH_ASSETS_FOR_DOWNLOAD: {
      const { page } = action.payload as FetchAssetsRequest;
      return {
        ...state,
        assets: {
          ...state.assets,
          download: {
            ...state.assets.download,
            loading: true,
            error: null,
            succeeded: false,
            items: page === 0 ? [] : _.get(state, "assets.download.items", [])
          }
        }
      }
    }

    case FETCHING_ASSETS_FOR_DOWNLOAD_SUCCEEDED: {
      const { response } = action.payload as FetchAssetsResponse;

      let existingItems: Asset[] = _.get(state, "assets.download.items", []);

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }
      records = _.uniqBy(records, 'id');

      return {
        ...state,
        assets: {
          ...state.assets,
          download: {
            loading: false,
            items: records,
            succeeded: true,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1
            },
            error: null
          }
        }
      }

    }

    case FETCHING_ASSETS_FOR_DOWNLOAD_FAILED: {
      const error = action.payload;

      return {
        ...state,
        assets: {
          ...state.assets,
          download: {
            ...state.assets.download,
            loading: false,
            error: error,
            succeeded: false
          }
        }
      }
    }

    case INVALIDATE_ASSETS_FOR_DOWNLOAD:
      return {
        ...state,
        assets: {
          ...state.assets,
          download: assetsInitialData
        }
      }

    case UPDATING_ASSET_REQUEST: {

      return {
        ...state,
        assets: {
          ...state.assets,
          update: {
            loading: true,
            error: null,
            succeeded: false
          }
        }
      }
    }

    case UPDATING_ASSET_SUCCEEDED:
      const updatedAsset = _.get(action, "payload.response") as Asset;

      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...state.assets.assetsList,
            items: _.get(state, "assets.assetsList.items", []).map((asset) => {
              if (asset.id === updatedAsset.id) {
                return {
                  ...updatedAsset,
                  analytics:{
                    ...asset.analytics
                  },
                  streamNames: asset.streamNames
                }
              }
              return asset;
            })
          },
          update: {
            loading: false,
            error: null,
            succeeded: true
          }
        }
      }

    case INVALIDATE_CONTENT_DATA:{
      return {
        ...state,
        creatingCluster: {
          loading: false,
          error: null,
          succeeded: false,
          item: null
        },
        deletingCluster: {
          loading: false,
          error: null,
          succeeded: false
        },
        updatingCluster: {
          loading: false,
          error: null,
          succeeded: false,
          item: null
        },
        assets:{
          ...state.assets,
          delete: {
            loading: false,
            error: null,
            succeeded: false
          },
          update: {
            loading: false,
            error: null,
            succeeded: false
          }
        }
      }
    }

    case FETCHING_ASSETS_FOR_CONTENT_SCORE_CARD: {

      const { page } = action.payload as FetchAssetsForContentScoreRequest;

      const assetsList = _.get(state, "assets.assetsList.items", []);

      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...assetsList,
            loading: true,
            error: null,
            succeeded: false,
            items:  page === 0 ? [] : _.get(state, "assets.assetsList.items", []),
             pagination: {
              ...state.assets.assetsList.pagination,
              currentPage : page
            }
          }
        }
      }
    }

    case FETCHING_ASSETS_FOR_CONTENT_SCORE_CARD_SUCCEEDED: { 

      const { response } = action.payload as FetchAssetsForContentScoreResponse;

      const existingItems: Asset[] = _.get(state, "assets.assetsList.items", []);

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, 'id');


      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...state.assets.assetsList,
            loading: false,
            error: null,
            succeeded: true,
            items: records as Asset[],
              pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1
            },
          }
        }
      }
    }

    case FETCHING_ASSETS_FOR_CONTENT_SCORE_CARD_FAILED: {

      const { error } = action.payload as FetchAssetsForContentScoreErrorResponse;

      return {
        ...state,
        assets: {
          ...state.assets,
          assetsList: {
            ...state.assets.assetsList,
            loading: false,
            error: error,
            succeeded: false
          }
        }
      }
    }

    case FETCHING_CONTENT_GRAPH_VALUES: {

      return {
        ...state,
        contentScore: {
          ...state.contentScore,
          graphData: {
            loading: true,
            error: null,
            succeeded: false,
            data: []
          }
        }
      }
    }

    case FETCHING_CONTENT_GRAPH_VALUES_SUCCEEDED: {
      const { response } = action.payload as FetchContentGraphValuesResponse;

      return {
        ...state,
        contentScore: {
          ...state.contentScore,
          graphData: {
            loading: false,
            error: null,
            succeeded: true,
            data: response
          }
        }
      }
    }

    case FETCHING_CONTENT_GRAPH_VALUES_FAILED: {
      const { error } = action.payload as FetchContentGraphValuesErrorResponse;

      return {
        ...state,
        contentScore: {
          ...state.contentScore,
          graphData: {
            loading: false,
            error: error,
            succeeded: false,
            data: []
          }
        }
      }
    }

    case FETCHING_CONTENT_SCORE: {

      return {
        ...state,
        contentScore: {
          ...state.contentScore,
          scoreData: {
            loading: true,
            error: null,
            succeeded: false,
            data: {
              ...state.contentScore.scoreData.data
            }
          }
        }
      }
    }

    case FETCHING_CONTENT_SCORE_SUCCEEDED: {
      const { response } = action.payload as FetchingContentScoreResponse;

      return {
        ...state,
        contentScore: {
          ...state.contentScore,
          scoreData: {
            loading: false,
            error: null,
            succeeded: true,
            data: response
          }
        }
      }
    }

    case FETCHING_CONTENT_SCORE_FAILED: {
      const { error } = action.payload as FetchingContentScoreErrorResponse;

      return {
        ...state,
        contentScore: {
          ...state.contentScore,
          scoreData: {
            loading: false,
            error: error,
            succeeded: false,
            data: {}
          }
        }
      }
    }

    default:
      return state;
  }
}

export default contentMapReducer;